/** Versioned, experience-specific contract shared by the host toolbar and Bifrost. */
export const DATA_FETCH_FEATURE = 'avpviz-data-fetch-v1';
export const DATA_FETCH_COMMAND = 'avpviz:data-fetch';
export const DATA_FETCH_STATE = 'avpviz:data-fetch-state';

export type DataFetchStrategy = 'STATIC' | 'DYNAMIC';
/** A command as the host control builds it; the channel adds `sessionId` when sending. */
export type DataFetchCommandBody = { type: typeof DATA_FETCH_COMMAND } & (
	| { action: 'connect' | 'disconnect' }
	| { action: 'set'; strategy: DataFetchStrategy }
);
export type DataFetchCommand = DataFetchCommandBody & { sessionId: string };

export type DataFetchState = {
	availableStrategies: DataFetchStrategy[];
	displayedStrategy: DataFetchStrategy | null;
	error: 'save-failed' | 'fetch-failed' | null;
	isSaving: boolean;
	requestedStrategy: DataFetchStrategy;
	sessionId: string;
	status: 'loading' | 'ready' | 'error';
	type: typeof DATA_FETCH_STATE;
};

export function isDataFetchStrategy(value: unknown): value is DataFetchStrategy {
	return value === 'STATIC' || value === 'DYNAMIC';
}

export function isDataFetchCommand(value: unknown): value is DataFetchCommand {
	if (!value || typeof value !== 'object') {
		return false;
	}
	const command = value as Record<string, unknown>;
	return (
		command.type === DATA_FETCH_COMMAND &&
		typeof command.sessionId === 'string' &&
		command.sessionId.length > 0 &&
		(command.action === 'connect' ||
			command.action === 'disconnect' ||
			(command.action === 'set' && isDataFetchStrategy(command.strategy)))
	);
}

export function isDataFetchState(value: unknown): value is DataFetchState {
	if (!value || typeof value !== 'object') {
		return false;
	}
	const state = value as Record<string, unknown>;
	return (
		state.type === DATA_FETCH_STATE &&
		typeof state.sessionId === 'string' &&
		isDataFetchStrategy(state.requestedStrategy) &&
		(state.displayedStrategy === null || isDataFetchStrategy(state.displayedStrategy)) &&
		Array.isArray(state.availableStrategies) &&
		state.availableStrategies.every(isDataFetchStrategy) &&
		['loading', 'ready', 'error'].includes(String(state.status)) &&
		typeof state.isSaving === 'boolean' &&
		(state.error === null || state.error === 'save-failed' || state.error === 'fetch-failed')
	);
}
